import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { nominaFiles } from "@/components/ConflittoNome";
import { BarraAllegati, Casella, useSelezione } from "@/components/SelezioneFile";
import { useAuth } from "@/context/AuthContext";
import { Plus, Receipt, BellRing, FileText, X } from "lucide-react";

const eur = (v) => `€ ${Number(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const dt = (s) => (s ? s.slice(0, 10).split("-").reverse().join("/") : "—");
const inp = "w-full px-3 py-2 rounded-xl border border-[var(--border)] text-sm";

function Nuova({ onClose, onSaved }) {
  const [f, setF] = useState({ numero: "", data: new Date().toISOString().slice(0, 10), imponibile: "", iva: "", totale: "", note: "", immobili_ids: [] });
  const [file, setFile] = useState(null);
  const [servizi, setServizi] = useState([]);
  useEffect(() => { api.get("/fotografo/servizi").then((r) => setServizi(r.data)).catch(() => {}); }, []);
  const tot = f.totale !== "" ? f.totale : ((Number(f.imponibile) || 0) + (Number(f.iva) || 0)).toFixed(2);
  const salva = async () => {
    const fd = new FormData();
    Object.entries({ ...f, totale: tot, immobili_ids: f.immobili_ids.join(",") }).forEach(([k, v]) => fd.append(k, v));
    if (file) fd.append("file", file);
    try { await api.post("/fatture-fotografo", fd); toast.success("Fattura inserita"); onSaved(); } catch (e) { toast.error(apiError(e)); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return createPortal(
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="fattura-modal">
      <div className="bg-white rounded-2xl p-5 w-full max-w-lg max-h-[90vh] overflow-y-auto space-y-3">
        <div className="flex items-center justify-between"><h2 className="font-display font-bold text-lg">Nuova fattura</h2><button onClick={onClose}><X size={18} /></button></div>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-xs font-semibold">Numero *<input data-testid="fatt-numero" className={inp} value={f.numero} onChange={set("numero")} /></label>
          <label className="text-xs font-semibold">Data *<input type="date" data-testid="fatt-data" className={inp} value={f.data} onChange={set("data")} /></label>
          <label className="text-xs font-semibold">Imponibile<input type="number" data-testid="fatt-imponibile" className={inp} value={f.imponibile} onChange={set("imponibile")} /></label>
          <label className="text-xs font-semibold">IVA<input type="number" data-testid="fatt-iva" className={inp} value={f.iva} onChange={set("iva")} /></label>
          <label className="text-xs font-semibold col-span-2">Totale<input type="number" data-testid="fatt-totale" className={inp} value={tot} onChange={set("totale")} /></label>
        </div>
        <div className="text-xs font-semibold">Immobili collegati (facoltativo)
          <div className="mt-1 max-h-32 overflow-y-auto border border-[var(--border)] rounded-xl p-2 space-y-1">
            {servizi.length === 0 ? <span className="text-[var(--muted)] font-normal">Nessun servizio.</span> : servizi.map((s) => (
              <label key={s.id} className="flex items-center gap-2 font-normal"><input type="checkbox" data-testid={`fatt-imm-${s.id}`} checked={f.immobili_ids.includes(s.id)}
                onChange={(e) => setF({ ...f, immobili_ids: e.target.checked ? [...f.immobili_ids, s.id] : f.immobili_ids.filter((x) => x !== s.id) })} />{s.indirizzo}{s.comune ? `, ${s.comune}` : ""}</label>
            ))}
          </div>
        </div>
        <label className="text-xs font-semibold block">File (PDF o immagine)<input type="file" accept=".pdf,image/*,.heic" data-testid="fatt-file" className="block mt-1 text-sm" onChange={(e) => { const x = e.target.files[0]; e.target.value = ""; if (x) nominaFiles([x]).then(([n]) => n && setFile(n)); }} />{file && <span data-testid="fatt-file-nome" className="block mt-1 font-normal text-[var(--muted)]">Allegato: {file.name}</span>}</label>
        <label className="text-xs font-semibold block">Note<textarea data-testid="fatt-note" className={inp} rows={2} value={f.note} onChange={set("note")} /></label>
        <button data-testid="fatt-salva" onClick={salva} className="w-full py-2.5 rounded-xl bg-[#111111] text-white font-semibold text-sm">Salva fattura</button>
      </div>
    </div>,
    document.body
  );
}

function Previsione({ f, onDone }) {
  const [entro, setEntro] = useState(f.pagheremo_entro || "");
  const [nota, setNota] = useState(f.nota_fornitore || "");
  const cambiato = entro !== (f.pagheremo_entro || "") || nota !== (f.nota_fornitore || "");
  const salva = async () => {
    try { await api.patch(`/fatture-fotografo/${f.id}/previsione`, { pagheremo_entro: entro || null, nota_fornitore: nota }); toast.success("Salvato"); onDone(); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="px-4 pb-4 grid grid-cols-1 sm:grid-cols-[180px_1fr_auto] gap-2 items-end" data-testid={`fattura-previsione-${f.id}`}>
      <label className="text-[11px] font-semibold text-[var(--muted)]">Pagheremo entro il<input type="date" data-testid={`fattura-entro-${f.id}`} className={inp} value={entro} onChange={(e) => setEntro(e.target.value)} /></label>
      <label className="text-[11px] font-semibold text-[var(--muted)]">Nota per il fornitore<input data-testid={`fattura-nota-${f.id}`} className={inp} value={nota} placeholder="es. pagamento a fine mese con bonifico" onChange={(e) => setNota(e.target.value)} /></label>
      <button data-testid={`fattura-previsione-salva-${f.id}`} disabled={!cambiato} onClick={salva} className="px-3 py-2 rounded-xl bg-[#111111] text-white text-xs font-semibold disabled:opacity-40">Salva</button>
    </div>
  );
}

function Fattura({ f, onDone, S }) {
  const { isDirezione, isSegreteria } = useAuth();
  const ok = f.stato === "saldato";
  const staff = isDirezione || isSegreteria;
  const [dataPag, setDataPag] = useState(new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Rome" }));
  const stato = async () => {
    try { await api.patch(`/fatture-fotografo/${f.id}/stato`, { stato: ok ? "da_saldare" : "saldato", data_pagamento: ok ? null : dataPag }); onDone(); } catch (e) { toast.error(apiError(e)); }
  };
  const ricorda = async () => { try { await api.post(`/fatture-fotografo/${f.id}/ricorda`); toast.success("Promemoria inviato ai Soci"); } catch (e) { toast.error(apiError(e)); } };
  const apri = async () => { try { const { data } = await api.get(`/fatture-fotografo/${f.id}/file`, { responseType: "blob" }); window.open(URL.createObjectURL(data), "_blank"); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div className="card-immo p-0 overflow-hidden" data-testid={`fattura-${f.id}`}>
      <div className={`h-2 ${ok ? "bg-emerald-500" : "bg-red-500"}`} />
      <div className="p-4 flex items-center gap-3 flex-wrap">
        {f.file_nome && <Casella inline on={S.sel.has(f.id)} onToggle={() => S.toggle(f.id)} testid={`fattura-sel-${f.id}`} />}
        <div className="flex-1 min-w-[200px]">
          <div className="font-semibold text-sm">Fattura n. {f.numero} · {dt(f.data)}{!isDirezione && !isSegreteria ? "" : ` · ${f.fotografo_nome}${f.categoria ? ` (${f.categoria})` : ""}`}</div>
          <div className="text-xs text-[var(--muted)]">Imponibile {eur(f.imponibile)} · IVA {eur(f.iva)} · <b className="text-[#111]">Totale {eur(f.totale)}</b>{f.note ? ` · ${f.note}` : ""}</div>
        </div>
        <span data-testid={`fattura-stato-${f.id}`} className={`px-2.5 py-1 rounded-full text-xs font-bold ${ok ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
          {ok ? `Pagata il ${dt(f.data_pagamento)}` : `Da pagare${staff && f.giorni_attesa != null ? ` · ${f.giorni_attesa} gg` : ""}`}</span>
        {staff && f.in_ritardo && <span data-testid={`fattura-ritardo-${f.id}`} className="px-2.5 py-1 rounded-full text-xs font-bold bg-red-600 text-white">In ritardo</span>}
        {f.file_nome && <button data-testid={`fattura-file-${f.id}`} onClick={apri} className="p-1.5 rounded-lg border border-[var(--border)]"><FileText size={14} /></button>}
        {isDirezione && (
          <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-[var(--border)] text-xs font-semibold" data-testid={`fattura-pagata-box-${f.id}`}>
            <input type="checkbox" data-testid={`fattura-pagata-${f.id}`} checked={ok} onChange={stato} /> Pagata
            {!ok && <input type="date" data-testid={`fattura-data-pag-${f.id}`} value={dataPag} onChange={(e) => setDataPag(e.target.value)} className="border border-[var(--border)] rounded px-1 py-0.5 font-normal" />}
          </label>
        )}
        {isSegreteria && !ok && <button data-testid={`fattura-ricorda-${f.id}`} onClick={ricorda} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-[var(--border)] text-xs font-semibold"><BellRing size={13} /> Ricorda ai Soci</button>}
      </div>
      {staff && !ok && <Previsione f={f} onDone={onDone} />}
      {!staff && !ok && (f.pagheremo_entro || f.nota_fornitore) && (
        <div className="px-4 pb-4 text-xs text-slate-700" data-testid={`fattura-info-pagamento-${f.id}`}>
          {f.pagheremo_entro && <div>Pagheremo entro il <b>{dt(f.pagheremo_entro)}</b></div>}
          {f.nota_fornitore && <div className="text-[var(--muted)]">{f.nota_fornitore}</div>}
        </div>
      )}
    </div>
  );
}

export default function LeMieFatture() {
  const { isFotografo } = useAuth();
  const [nuova, setNuova] = useState(false);
  const [k, setK] = useState(0);
  if (!isFotografo) { window.location.replace("/marketing?tab=fornitori"); return null; }
  return (
    <div className="fade-up" data-testid="fatture-page">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <h1 className="font-display text-2xl font-extrabold flex items-center gap-2 flex-1"><Receipt className="text-[#FC82ED]" /> Le mie fatture</h1>
        <button data-testid="fattura-nuova" onClick={() => setNuova(true)} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#111111] text-white text-sm font-semibold"><Plus size={15} /> Nuova fattura</button>
      </div>
      <FattureElenco key={k} />
      {nuova && <Nuova onClose={() => setNuova(false)} onSaved={() => { setNuova(false); setK(k + 1); }} />}
    </div>
  );
}

export function FattureElenco() {
  const [list, setList] = useState(null);
  const S = useSelezione();
  const load = () => api.get("/fatture-fotografo").then((r) => setList(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);
  return !list ? <div className="skeleton h-40 rounded-2xl" /> : list.length === 0 ? <div className="card-immo p-8 text-center text-[var(--muted)]">Nessuna fattura.</div>
    : <div className="space-y-3" data-testid="fatture-elenco">
      <BarraAllegati S={S} ids={list.filter((f) => f.file_nome).map((f) => f.id)} tipo="fattura" id="" onDone={load} testid="fatture-allegati" />
      {list.map((f) => <Fattura key={f.id} f={f} onDone={load} S={S} />)}</div>;
}

export function FattureDaSaldare() {
  const nav = (p) => { window.location.href = p; };
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/fatture-fotografo", { params: { scadute: 1 } }).then((r) => setList(r.data)).catch(() => {}); }, []);
  if (!list.length) return null;
  return (
    <button onClick={() => nav("/marketing?tab=fornitori")} data-testid="home-fatture-scadute" className="w-full text-left mb-4 rounded-2xl border-2 border-red-200 bg-red-50 p-4">
      <div className="font-bold text-sm text-red-700 flex items-center gap-2"><Receipt size={16} /> {list.length} fatture fornitori marketing da saldare da più di 15 giorni</div>
      <div className="text-xs text-red-700/80 mt-1">{list.slice(0, 4).map((f) => `${f.fotografo_nome} n.${f.numero} ${eur(f.totale)} (${f.giorni_attesa} gg)`).join(" · ")}</div>
    </button>
  );
}
